/**
 * Zenith Pulse Training Intelligence Engine
 * Analyses workout history, body metrics, goals, frequency — produces coach-grade output.
 */
(function (global) {
  'use strict';

  const AU_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL'];

  function normName(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .trim();
  }

  function daysAgo(dateStr) {
    return (Date.now() - new Date(dateStr).getTime()) / 86400000;
  }

  function parseNum(v) {
    const n = parseFloat(String(v || '').replace(/[^\d.]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  function e1rm(weight, reps) {
    const r = Math.max(1, parseInt(reps, 10) || 1);
    return weight * (1 + r / 30);
  }

  /** Best working set per workout session for a movement pattern */
  function getExerciseSessions(workouts, pattern) {
    const re = typeof pattern === 'string' ? new RegExp(pattern, 'i') : pattern;
    const sessions = [];

    (workouts || []).forEach((w) => {
      let best = null;
      (w.exercises || []).forEach((ex) => {
        if (!re.test(ex.name || '')) return;
        const weight = parseNum(ex.weight);
        const reps = parseInt(ex.reps, 10) || 0;
        const sets = parseInt(ex.sets, 10) || 1;
        if (weight <= 0) return;
        const score = e1rm(weight, reps);
        const row = {
          date: w.date,
          workoutId: w.id,
          name: ex.name,
          weight,
          reps,
          sets,
          rpe: parseNum(ex.rpe),
          volume: weight * reps * sets,
          e1rm: score,
          notes: ex.notes || '',
        };
        if (!best || score > best.e1rm) best = row;
      });
      if (best) sessions.push(best);
    });

    sessions.sort((a, b) => new Date(b.date) - new Date(a.date));
    return sessions;
  }

  function sessionsInRange(sessions, minDays, maxDays) {
    return sessions.filter((s) => {
      const d = daysAgo(s.date);
      return d >= minDays && d < maxDays;
    });
  }

  function bestInSessions(sessions) {
    if (!sessions.length) return null;
    return sessions.reduce((a, b) => (b.e1rm > a.e1rm ? b : a));
  }

  /**
   * Week-over-week lift analysis — produces messages like the user's bench example.
   */
  function analyzeLiftProgression(displayName, pattern, workouts, goal) {
    const sessions = getExerciseSessions(workouts, pattern);
    if (sessions.length < 1) return null;

    const thisWeek = bestInSessions(sessionsInRange(sessions, 0, 7));
    const lastWeek = bestInSessions(sessionsInRange(sessions, 7, 14));

    if (!thisWeek) return null;

    if (!lastWeek) {
      return (
        'Current ' +
        displayName +
        ': ' +
        thisWeek.weight +
        'kg × ' +
        thisWeek.reps +
        ' (RPE ' +
        (thisWeek.rpe || '—') +
        '). Log again next week to unlock week-over-week progression analysis.'
      );
    }

    const wDelta = Math.round((thisWeek.weight - lastWeek.weight) * 10) / 10;
    const repDelta = thisWeek.reps - lastWeek.reps;
    let nextWeight = thisWeek.weight;

    if (wDelta > 0 && repDelta >= -1) {
      nextWeight = Math.round((thisWeek.weight + 2.5) * 10) / 10;
      return (
        'Great progress. You increased your ' +
        displayName +
        ' by ' +
        wDelta +
        'kg while ' +
        (repDelta >= 0 ? 'maintaining or improving reps' : 'trading slightly fewer reps for load') +
        ' (last week ' +
        lastWeek.weight +
        'kg × ' +
        lastWeek.reps +
        ', this week ' +
        thisWeek.weight +
        'kg × ' +
        thisWeek.reps +
        '). Next week aim for ' +
        nextWeight +
        'kg for 6–8 reps.'
      );
    }

    if (wDelta === 0 && repDelta > 0) {
      return (
        displayName +
        ' volume progressed via reps (+' +
        repDelta +
        ' reps at ' +
        thisWeek.weight +
        'kg). Next session: same weight for +' +
        (repDelta + 1) +
        ' reps, or add 2.5kg if all sets hit RPE ≤8.'
      );
    }

    if (wDelta < 0) {
      return (
        displayName +
        ' load dipped vs last week — likely fatigue. Recommendation: deload 10% to ' +
        Math.round(lastWeek.weight * 0.9 * 10) / 10 +
        'kg and rebuild, or prioritise sleep and nutrition before pushing load again.'
      );
    }

    return (
      displayName +
      ' held steady at ' +
      thisWeek.weight +
      'kg. ' +
      (goal === 'muscle_gain'
        ? 'Add a back-off set or increase reps before adding load.'
        : 'Consider a 2.5kg jump if RPE was below 7.')
    );
  }

  function bodyweightTrend(measurements) {
    const sorted = (measurements || [])
      .filter((m) => m.weight)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (sorted.length < 2) return null;
    const latest = sorted[0].weight;
    const prior = sorted[Math.min(4, sorted.length - 1)].weight;
    const delta = Math.round((latest - prior) * 10) / 10;
    const weeks = Math.max(1, Math.round(daysAgo(sorted[Math.min(4, sorted.length - 1)].date) / 7));
    return { latest, delta, weeks, perWeek: Math.round((delta / weeks) * 100) / 100 };
  }

  function trainingFrequency(workouts) {
    const last7 = (workouts || []).filter((w) => daysAgo(w.date) <= 7).length;
    const last30 = (workouts || []).filter((w) => daysAgo(w.date) <= 30).length;
    return { last7, last30, avgPerWeek: Math.round((last30 / 4.3) * 10) / 10 };
  }

  function needsDeload(workouts) {
    const recent = (workouts || []).filter((w) => daysAgo(w.date) <= 14);
    if (recent.length < 5) return false;
    let highRpe = 0;
    recent.forEach((w) => {
      (w.exercises || []).forEach((ex) => {
        if (parseNum(ex.rpe) >= 9) highRpe++;
      });
    });
    const freq = trainingFrequency(workouts);
    return highRpe >= 6 || freq.last7 >= 6;
  }

  function generateDailyPlan(state) {
    const goals = state.profile.goals || [];
    const freq = state.profile.trainingDaysPerWeek || 4;
    const day = new Date().getDay();
    const primary = goals[0] || 'muscle_gain';

    const templates = {
      muscle_gain: [
        ['Push — chest, shoulders, triceps', 'Bench Press 4×6–8', 'Incline DB Press 3×10', 'Lateral Raises 3×15'],
        ['Pull — back, biceps', 'Barbell Row 4×6–8', 'Lat Pulldown 3×10', 'Face Pulls 3×15'],
        ['Legs — quads, hamstrings, glutes', 'Squat 4×6–8', 'RDL 3×8', 'Leg Curl 3×12'],
        ['Upper pump', 'Incline Press 3×12', 'Cable Row 3×12', 'Arms 4×12'],
        ['Lower strength', 'Squat 5×5', 'Leg Press 3×10', 'Calves 4×15'],
        ['Active recovery', 'Walk 30 min + mobility 20 min'],
        ['Rest or light cardio', 'Zone 2 — 40 min'],
      ],
      weight_loss: [
        ['Full body + conditioning', 'Circuit 40 min — compounds + bike'],
        ['Strength maintenance', 'Squat 3×8', 'Press 3×8', 'Row 3×8'],
        ['Intervals', 'Run/watt bike 6×2 min hard / 2 min easy'],
        ['Upper', 'Push/Pull supersets 45 min'],
        ['Lower', 'Legs 45 min + steps target 10k'],
        ['Long walk', '60 min brisk'],
        ['Rest', 'Stretching + hydration focus'],
      ],
      hyrox: [
        ['Engine — run + erg', '5km easy', '500m row', '50 wall balls'],
        ['Strength', 'Squat 4×6', 'Deadlift 3×5', 'Sled push practice'],
        ['HYROX simulation', '1km run → 80m sled → 1km run → 100 wall balls'],
        ['Recovery run', '30 min zone 2'],
        ['Intervals', '8×400m run @ 5k pace'],
        ['Mixed modal', 'Burpees, lunges, farmers carry'],
        ['Rest', 'Mobility + foam roll'],
      ],
      running: [
        ['Easy run', '5–8 km zone 2'],
        ['Strength', 'Single leg work 30 min'],
        ['Tempo', '4 km @ threshold'],
        ['Rest', 'Stretch'],
        ['Intervals', '6×800m'],
        ['Long run', '10–14 km easy'],
        ['Cross train', 'Bike 45 min'],
      ],
      strength: [
        ['Squat focus', 'Squat 5×5', 'Accessories 3×8'],
        ['Bench focus', 'Bench 5×5', 'Triceps 3×10'],
        ['Deadlift focus', 'Deadlift 4×4', 'Back 3×8'],
        ['OHP', 'Press 4×6', 'Pull-ups 4×max'],
        ['Accessory', 'Weak point work 45 min'],
        ['Conditioning', 'Sled / carries 30 min'],
        ['Rest', 'Full rest'],
      ],
    };

    const week = templates[primary] || templates.muscle_gain;
    const idx = day % 7;
    const label = week[idx][0];
    const exercises = week[idx].slice(1);

    if (freq <= 3 && [1, 3, 5].includes(day)) {
      return { focus: 'Rest day (low frequency programme)', exercises: ['Mobility 20 min', 'Optional walk'], type: 'recovery' };
    }

    return { focus: label, exercises, type: primary };
  }

  function generateWeeklyPlan(state) {
    const daily = [];
    const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - d.getDay() + 1 + i);
      const fakeState = { ...state, profile: { ...state.profile } };
      const origDay = new Date().getDay();
      const plan = generateDailyPlanForDay(fakeState, i === 0 ? 1 : i === 1 ? 2 : i === 2 ? 3 : i === 3 ? 4 : i === 4 ? 5 : i === 5 ? 6 : 0);
      daily.push(names[i] + ': ' + plan.focus);
    }
    return daily;
  }

  function generateDailyPlanForDay(state, dayIndex) {
    const goals = state.profile.goals || [];
    const primary = goals[0] || 'muscle_gain';
    const templates = {
      muscle_gain: ['Push', 'Pull', 'Legs', 'Upper', 'Lower', 'Recovery', 'Rest'],
      weight_loss: ['Circuit', 'Strength', 'Intervals', 'Upper', 'Lower', 'Walk', 'Rest'],
      hyrox: ['Engine', 'Strength', 'HYROX sim', 'Recovery', 'Intervals', 'Mixed', 'Rest'],
      running: ['Easy', 'Strength', 'Tempo', 'Rest', 'Intervals', 'Long', 'Cross'],
      strength: ['Squat', 'Bench', 'Deadlift', 'OHP', 'Accessory', 'Conditioning', 'Rest'],
    };
    const t = templates[primary] || templates.muscle_gain;
    return { focus: t[dayIndex] || 'Training' };
  }

  function generateFullReport(state) {
    const workouts = state.workouts || [];
    const goals = state.profile.goals || [];
    const primary = goals[0] || 'muscle_gain';
    const freq = trainingFrequency(workouts);
    const bw = bodyweightTrend(state.measurements);
    const deload = needsDeload(workouts);

    const lifts = [
      { name: 'Bench Press', pattern: 'bench' },
      { name: 'Squat', pattern: 'squat' },
      { name: 'Deadlift', pattern: 'deadlift' },
      { name: 'Overhead Press', pattern: 'overhead|ohp|press' },
      { name: 'Barbell Row', pattern: 'row' },
    ];

    const exerciseInsights = lifts
      .map((l) => {
        const msg = analyzeLiftProgression(l.name, l.pattern, workouts, primary);
        return msg ? { exercise: l.name, message: msg } : null;
      })
      .filter(Boolean);

    const report = {
      generatedAt: new Date().toISOString(),
      headline: '',
      dailyPlan: generateDailyPlan(state),
      weeklyPlan: generateWeeklyPlan(state),
      exerciseInsights,
      strength: [],
      muscleGain: [],
      weightLoss: [],
      hyrox: [],
      recovery: [],
      deload: null,
    };

    if (deload) {
      report.deload = {
        active: true,
        message:
          'Deload recommended: high RPE density or 6+ sessions in 7 days detected. Cut volume 40% and intensity 10–15% for one week.',
      };
      report.recovery.push('Sleep 7–9h, protein at 1.8g/kg, reduce caffeine after 2pm.');
    }

    if (bw) {
      const dir = bw.delta > 0.3 ? 'up' : bw.delta < -0.3 ? 'down' : 'stable';
      report.headline =
        'Bodyweight ' +
        dir +
        ' ' +
        Math.abs(bw.delta) +
        'kg over ' +
        bw.weeks +
        ' weeks (now ' +
        bw.latest +
        'kg). ';
    } else {
      report.headline = 'Training frequency: ' + freq.last7 + ' sessions this week. ';
    }

    report.headline += freq.last7 < 3 ? 'Increase consistency to hit your programme targets.' : 'Solid training rhythm — stay progressive.';

    if (primary === 'muscle_gain') {
      report.muscleGain.push('Prioritise progressive overload on compounds 2×/week each.');
      report.muscleGain.push('Protein target: ' + Math.round((parseNum(state.profile.weightKg) || 80) * 2) + 'g/day.');
      if (exerciseInsights.length) report.muscleGain.push(exerciseInsights[0].message);
    }

    if (primary === 'weight_loss') {
      report.weightLoss.push('Maintain 300–500 kcal deficit; keep lifting 3×/week to preserve muscle.');
      report.weightLoss.push('Add 8–10k steps daily; schedule 2 conditioning sessions.');
      if (bw && bw.delta > 0) report.weightLoss.push('Weight trend up — review nutrition adherence.');
    }

    if (primary === 'hyrox' || goals.includes('hyrox')) {
      report.hyrox.push('Weekly: 2 runs, 1 HYROX simulation, 2 strength sessions.');
      report.hyrox.push('Practice sled push, wall balls, and rowing 500m repeats.');
    }

    if (primary === 'strength' || goals.includes('strength')) {
      report.strength.push('Heavy compounds first; track top set RPE 7–8.5.');
      if (exerciseInsights[0]) report.strength.push(exerciseInsights[0].message);
    }

    if (freq.last7 >= (state.profile.trainingDaysPerWeek || 5)) {
      report.recovery.push('You hit your planned frequency — schedule one full rest day.');
    } else {
      report.recovery.push('Room for ' + ((state.profile.trainingDaysPerWeek || 4) - freq.last7) + ' more sessions this week.');
    }

    return report;
  }

  function recommendSize(input) {
    const height = parseNum(input.height) || 180;
    const weight = parseNum(input.weight) || 80;
    const chest = parseNum(input.chest) || height * 0.59;
    const waist = parseNum(input.waist) || height * 0.44;
    const shoulders = parseNum(input.shoulders) || chest * 1.08;
    const build = input.build || 'athletic';
    const fit = input.fitPreference || 'regular';

    let score = chest * 1.35 + shoulders * 0.25 + waist * 0.15 + height * 0.08 + weight * 0.12;
    if (build === 'slim') score -= 8;
    if (build === 'muscular') score += 12;
    if (build === 'athletic') score += 4;

    let sizeIdx = 2;
    if (score < 118) sizeIdx = 0;
    else if (score < 128) sizeIdx = 1;
    else if (score < 142) sizeIdx = 2;
    else if (score < 155) sizeIdx = 3;
    else if (score < 168) sizeIdx = 4;
    else sizeIdx = 5;

    if (fit === 'oversized' || fit === 'relaxed') sizeIdx = Math.min(5, sizeIdx + 1);
    if (fit === 'compression') sizeIdx = Math.max(0, sizeIdx - 1);

    const size = AU_SIZES[sizeIdx];
    let confidence = 88 + Math.min(6, Math.round((chest + waist) % 7));
    if (!input.chest || !input.waist) confidence -= 12;

    const buildLabel = build.charAt(0).toUpperCase() + build.slice(1);
    const fitLabel =
      fit === 'compression' ? 'compression' : fit === 'oversized' ? 'oversized' : 'regular';

    const reason =
      'Based on chest (' +
      chest +
      'cm), shoulder width (' +
      shoulders +
      'cm), waist (' +
      waist +
      'cm) and ' +
      buildLabel.toLowerCase() +
      ' build, size ' +
      size +
      ' will provide the intended Zenith Pulse ' +
      fitLabel +
      ' fit.';

    return { size, confidence, reason, letter: size.replace('2XL', 'XXL').charAt(0) };
  }

  global.ZenithTrainingAI = {
    normName,
    getExerciseSessions,
    analyzeLiftProgression,
    generateFullReport,
    generateDailyPlan,
    generateWeeklyPlan,
    recommendSize,
    bodyweightTrend,
    trainingFrequency,
    needsDeload,
    e1rm,
  };
})(typeof window !== 'undefined' ? window : global);
